import { Component, OnInit, Input, ChangeDetectorRef, OnDestroy, ViewChild, ViewChildren, QueryList, EventEmitter, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { Collection, IAreaCollectionForm } from 'src/app/global/services/collections/collections.model';
import { Icon, Days, ScheduleFrequencies, ScheduleType, MinifiedShape } from '@bciti/types';
import { areasSelector, availableLanguagesSelector, organisationSelector } from 'src/app/global/store/global.selectors';
import { BcitiTextSelectOption } from '../../forms/text-select/bciti-text-select.component';
import { FormBuilder, FormControl, Validators, FormGroup, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import * as uuid from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil, tap, startWith, take, filter, map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { GetOrganisationAreasAction, GetOrganisationAreasSuccessAction, GetOrganisationAreasErrorAction } from 'src/app/global/store/global.actions';
import { Actions, ofType } from '@ngrx/effects';
import { CollectionScheduleComponent } from '../collection-schedule/collection-schedule.component';
import * as Moment from 'moment';
import { extendMoment } from 'moment-range';
import * as _ from 'lodash';
import { DateTime } from 'luxon';

export const moment = extendMoment(Moment);

@Component({
    selector: 'app-collection-form',
    templateUrl: './collection-form.component.html',
    styleUrls: ['./collection-form.component.scss'],
})
export class CollectionFormComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    @Input() collection: Collection;
    @Input() title: string;
    @Output() submitForm = new EventEmitter<Collection>();
    @Input() submitLoading = false;
    @Input() isEditMode: boolean = false;

    public orgTimeZone: string;

    public userTimeZone;

    colorsList = ['#363E41', '#6AB547', '#3359D9', '#F6A513', '#13DBF6', '#EE4266', '#8A2BD6', '#C8E12D', '#A46A01', '#F6DE00'];
    enabledIcon = [Icon.RECYCLING, Icon.RECYCLING_BIN, Icon.TRASH_BIN, Icon.TRASH_BIN_2, Icon.TREE, Icon.COMPOST, Icon.RESTAURANT, Icon.WAREHOUSE_CART_PACKAGES];
    iconsList = Object.values(Icon).filter((icon) => this.enabledIcon.includes(icon));
    minifiedShapesList = Object.values(MinifiedShape);

    loading = false;
    aerasLoading: boolean;

    areas$ = this.store.select(areasSelector).pipe(
        takeUntil(this.destroy$),
        map((areas: any[]) => {
            return areas.map((area) => {
                return {
                    id: area._id,
                    name: area.name,
                    isSelected: false,
                };
            });
        }),
    );

    langs: string[];
    frequencies: BcitiTextSelectOption[] = [];
    days: any[] = [];

    form = this.fb.group({
        name: this.fb.group({}),
        areas: new FormControl([], [Validators.required]),
        color: new FormControl('', [Validators.required]),
        icon: new FormControl('', [Validators.required]),
        minifiedShape: new FormControl('', [Validators.required]),
        defaultSchedule: new FormControl({}, [Validators.required]),
        customSchedules: this.fb.group({}),
    });

    collectionFormSubmitted = false;

    customSchedulesIds: string[] = [];
    @ViewChild('defaultSchedule')
    private defaultScheduleVC: CollectionScheduleComponent;

    @ViewChildren('customSchedule')
    private customSchedulesQl: QueryList<CollectionScheduleComponent>;

    get currentLang(): string {
        return this.translate.currentLang;
    }

    get areasForm(): AbstractControl {
        return this.form.get('areas');
    }
    get nameForm(): AbstractControl {
        return this.form.get('name');
    }
    get defaultScheduleForm(): AbstractControl {
        return this.form.get('defaultSchedule');
    }

    constructor(
        private router: Router,
        private store: Store<State>,
        private actions$: Actions,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.actions$
            .pipe(
                ofType(GetOrganisationAreasAction),
                tap(() => (this.aerasLoading = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                ofType(GetOrganisationAreasSuccessAction, GetOrganisationAreasErrorAction),
                tap(() => (this.aerasLoading = false)),
            )
            .subscribe();

        this.areas$
            .pipe(
                take(1),
                filter((areas) => areas.length === 0),
                tap(() => this.store.dispatch(GetOrganisationAreasAction())),
            )
            .subscribe();

        this.store.select(organisationSelector).subscribe((org) => {
            this.orgTimeZone = org.timeZone;
        });

        this.store.select(availableLanguagesSelector).subscribe((langs) => {
            this.langs = [this.currentLang, ...[...langs].filter((l) => this.currentLang !== l)];
            if (this.langs.length === 1) {
                const lang = this.langs[0];
                (this.form.get('name') as FormGroup).addControl(lang, new FormControl('', [Validators.required]));
                return;
            }
            this.langs.forEach((lang) => {
                const nameControl = new FormControl('', [Validators.required]);
                (this.form.get('name') as FormGroup).addControl(lang, nameControl);
            });
        });

        this.formatDaysAndFrequencies();
        this.translateDays();
        this.translateFrequencies();

        this.translate.onLangChange
            .pipe(
                startWith(''),
                takeUntil(this.destroy$),
                tap(() => {
                    this.translateDays();
                    this.translateFrequencies();
                }),
            )
            .subscribe();

        this.userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (this.isEditMode) {
            this.configureForm(this.collection);
        } else {
            this.defaultScheduleForm.setValidators([Validators.required]);
        }
    }

    configureForm(collection: Collection) {
        let collect = _.cloneDeep(collection);
        let defSchedules = collect.schedules
            .filter((schedule) => schedule.type === ScheduleType.DEFAULT)
            .map((schedule) => {
                return {
                    frequency: schedule.frequency,
                    from: this.convertToUserTimezone(schedule.from),
                    to: schedule.to ? this.convertToUserTimezone(schedule.to) : null,
                    fromTime: schedule.fromTime,
                    days: schedule.days,
                };
            });

        let customSchedules = collection.schedules
            .filter((schedule) => schedule.type === ScheduleType.CUSTOM)
            .map((schedule) => {
                return {
                    frequency: schedule.frequency,
                    from: this.convertToUserTimezone(schedule.from),
                    to: schedule.to ? this.convertToUserTimezone(schedule.to) : null,
                    fromTime: schedule.fromTime,
                    days: schedule.days,
                };
            });

        this.cdr.detectChanges();

        this.form.patchValue({
            name: collection.name,
            color: collection.color,
            icon: collection.icon,
            minifiedShape: collection.minifiedShape ?? null,
            areas: (collection.areas as IAreaCollectionForm[]).map((area) => ({ id: area._id, name: area.name, isSelected: true })),
        });

        this.areas$ = this.areas$.pipe(
            map((areas: any[]) => {
                areas.forEach((area) => {
                    this.areasForm.value.forEach((areaSelected) => {
                        if (area.id === areaSelected.id) {
                            area.isSelected = true;
                        }
                    });
                });
                return areas;
            }),
        );

        this.defaultScheduleForm.setValue({
            frequency: defSchedules[0].frequency,
            from: defSchedules[0].from,
            to: defSchedules[0]?.to ?? null,
            fromTime: defSchedules[0].fromTime,
            days: defSchedules[0].days,
        });

        if (customSchedules.length > 0) {
            customSchedules.forEach((schedule) => {
                const id = this.startCreatingCustomSchedule();
                (this.form.get('customSchedules') as FormGroup).get(id).patchValue(schedule);
                (this.form.get('customSchedules') as FormGroup).get(id).updateValueAndValidity();
            });
        }
        this.defaultScheduleForm.updateValueAndValidity();
    }

    private formatDaysAndFrequencies() {
        Object.values(Days).forEach((value) => {
            this.days.push({
                value: value,
                label: value,
            });
        });
        Object.values(ScheduleFrequencies).forEach((frequency) => {
            this.frequencies.push({
                label: frequency,
                value: frequency,
            });
        });
    }

    private translateDays(): void {
        this.days.forEach((day) => {
            day.label = this.translate.instant('pages.collections.days-slice.' + day.value);
        });
    }

    private translateFrequencies(): void {
        this.frequencies.forEach((frequency) => {
            frequency.label = this.translate.instant('pages.collections.frequency.' + frequency.value);
        });
    }

    removeArea(removedArea: any): void {
        this.areasForm.patchValue(this.areasForm.value.filter((area) => area.id !== removedArea.id));
        this.areas$ = this.areas$.pipe(
            map((areas: any[]) => {
                areas.forEach((area) => {
                    if (area.id === removedArea.id) {
                        area.isSelected = false;
                    }
                });
                return areas;
            }),
        );
    }

    startCreatingCustomSchedule(): string {
        const id = uuid.v4();
        this.customSchedulesIds.push(id);
        (this.form.get('customSchedules') as FormGroup).addControl(id, new FormControl({}, [Validators.required]));
        this.cdr.detectChanges();
        return id;
    }

    handleCancelCreate(id: string) {
        this.customSchedulesIds = this.customSchedulesIds.filter((customSchedulesId) => customSchedulesId !== id);
        (this.form.get('customSchedules') as FormGroup).removeControl(id);
        this.cdr.detectChanges();
    }

    private markFormGroupTouched(formGroup: FormGroup) {
        (Object as any).values(formGroup.controls).forEach((control) => {
            control.markAsTouched();
            if (control.controls) {
                this.markFormGroupTouched(control);
            }
        });
    }

    saveCollection() {
        this.form.updateValueAndValidity();
        this.collectionFormSubmitted = true;
        // Mark all fields as touched, show errors if any.
        this.markFormGroupTouched(this.form);
        this.markFormGroupTouched(this.defaultScheduleVC.scheduleForm);
        // Mark all fields in customSchedules as touched, show errors if any.
        this.customSchedulesQl.forEach((customSchedule) => {
            this.markFormGroupTouched(customSchedule.scheduleForm);
        });

        const defaultScheduleVCErrors =
            this.defaultScheduleVC.fromTime.errors ||
            this.defaultScheduleVC.days.errors ||
            this.defaultScheduleVC.frequency.errors ||
            this.defaultScheduleVC.from.errors ||
            this.defaultScheduleVC.scheduleForm.errors;

        let customSchedulesQlErrors = this.customSchedulesQl.map((customSchedule) => {
            const form = customSchedule.scheduleForm.controls;
            return form.from.errors || form.to.errors || form.days.errors || form.frequency.errors || form.fromTime.errors || customSchedule.scheduleForm.errors;
        });

        // const overlapError = this.customSchedulesQl.some(cs => cs.overlapError);

        if (customSchedulesQlErrors.every((error) => error === null)) {
            customSchedulesQlErrors = null;
        }

        if (this.form.invalid || defaultScheduleVCErrors || customSchedulesQlErrors) {
            this.scrollToFirstInvalidControl();
            return;
        }

        this.submitForm.emit(this.buildPayload());
    }

    private scrollToFirstInvalidControl() {
        setTimeout(() => {
            const invalidControlsList = document.querySelectorAll('.ng-invalid');
            invalidControlsList.forEach((control) => {
                if (control.classList.contains('scroll-margin')) {
                    control.classList.add('scroll-margin');
                }
            });
            let firstInvalidControl = invalidControlsList[1];
            if (firstInvalidControl) {
                firstInvalidControl.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
                (firstInvalidControl as HTMLElement).focus();
            }
        }, 0);
    }

    buildPayload(): Collection {
        const defaultScheduleValue = this.form.controls.defaultSchedule.value as any;
        const formattedDefaultFromDate = this.convertToCityTimezone(defaultScheduleValue.from);
        let defaultToDate = defaultScheduleValue.to;
        const formattedDefaultToDate = defaultToDate && defaultToDate !== '' ? this.convertToCityTimezone(defaultScheduleValue.to) : null;
        let defaultFrequency = defaultScheduleValue?.frequency?.value ?? defaultScheduleValue?.frequency;

        let defaultSchedule = {
            ...defaultScheduleValue,
            frequency: defaultFrequency,
            from: formattedDefaultFromDate,
            to: formattedDefaultToDate,
            type: ScheduleType.DEFAULT,
        };
        let customSchedules = [];
        this.customSchedulesQl.forEach((customSchedule) => {
            let customFrequency = customSchedule.value?.frequency?.value ?? customSchedule.value?.frequency;
            let formattedFromDate = this.convertToCityTimezone(customSchedule.value.from);
            let formattedToDate = this.convertToCityTimezone(customSchedule.value.to);

            customSchedules.push({
                ...customSchedule.value,
                from: formattedFromDate,
                to: formattedToDate,
                type: ScheduleType.CUSTOM,
                frequency: customFrequency,
            });
        });

        let aeras = this.areasForm.value.filter((area) => area.isSelected).map((area) => area.id);
        let payload;
        payload = {
            name: this.nameForm.value,
            areas: aeras,
            color: this.form.controls.color.value,
            icon: this.form.controls.icon.value,
            minifiedShape: this.form.controls.minifiedShape.value,
            schedules: [defaultSchedule, ...customSchedules],
        };

        if (this.isEditMode) {
            payload = {
                ...payload,
                _id: this.collection._id,
            };
        }
        return payload;
    }

    public goBack() {
        this.router.navigate(['/collections']);
    }

    /**
     * Convert date to city timezone.
     * Dates set in datepicker are in user timezone, convert them to city timezone to prevent **dayshift** issues
     *
     * @param {Date} date - The date to be converted
     */
    private convertToCityTimezone(date) {
        return DateTime.fromJSDate(date, { zone: this.userTimeZone }).setZone(this.orgTimeZone, { keepLocalTime: true }).setZone('utc').toString();
    }

    /**
     * Convert date to city timezone.
     * Dates displayed in datepicker are in user timezone, convert utc server dates to user tz to prevent **display** issues
     *
     * @param {Date} date - The date to be converted
     */
    private convertToUserTimezone(date) {
        return DateTime.fromISO(date, { zone: 'utc' }).setZone(this.orgTimeZone).setZone(this.userTimeZone, { keepLocalTime: true }).toJSDate();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
